import {Box, Flex, Heading, Text, VStack} from '@tempo/core';

import {LAYOUT_PX} from '@modules/landing/layout';

export type LegalSection = {
	id?: string;
	heading?: string;
	body: string;
};

type LegalPageProps = {
	title: string;
	/** Plain text body (rendered with whiteSpace="pre-line"). Ignored when `sections` is provided. */
	body?: string;
	/** Structured sections (heading + body). Preferred over `body`. */
	sections?: LegalSection[];
	testId?: string;
};

export const LegalPage = ({
	title,
	body,
	sections,
	testId = 'LegalPage',
}: LegalPageProps) => {
	const resolvedSections: LegalSection[] =
		sections && sections.length > 0 ? sections : [{body: body ?? ''}];

	return (
		<Flex
			data-testid={testId}
			flex="1"
			direction="column"
			alignItems="center"
			px={LAYOUT_PX}
			py={['spacing.xl', 'spacing.2xl']}
		>
			<Box maxW="1120px" w="100%">
				<Heading
					as="h1"
					textAlign="center"
					variant={['heading.l', 'display.s', 'display.m']}
					mb="spacing.xl"
					color="text.neutral.primary.default"
				>
					{title}
				</Heading>
				<VStack
					align="stretch"
					spacing="spacing.xl"
					data-testid="LegalPage-sections"
				>
					{resolvedSections.map((section, idx) => (
						<Box
							key={idx}
							id={section.id}
							data-testid={`LegalPage-section-${idx}`}
						>
							{section.heading ? (
								<Heading
									as="h2"
									variant="heading.s"
									mb="spacing.m"
									color="text.neutral.primary.default"
								>
									{section.heading}
								</Heading>
							) : null}
							<Text
								whiteSpace="pre-line"
								variant="body.l.default"
								color="text.neutral.primary.default"
							>
								{section.body}
							</Text>
						</Box>
					))}
				</VStack>
			</Box>
		</Flex>
	);
};
